import {
  AnimationProvider,
  Card,
  DepositToWalletButton,
  DownloadFromCloudButton,
  EarnButton,
  ErrorAlert,
  LoadingAlert,
  PortfolioAssetsSection,
  RewardsSummarySection,
  TradeButton,
  WarningAlert,
  WithdrawFromWalletButton,
} from '@/components';
import { useTransactions } from '@/components/providers';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import APP_CONFIG from '@/config/app';
import { WalletUnderlyingYieldBearingAssetInterface } from '@/interfaces';
import { URLS } from '@/utils/constants';
import { logError, logInfo, logWarning } from '@/utils/logger';
import { WALLET } from '@/utils/strings';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const { MAIN_GAP } = APP_CONFIG.STYLES;
const spacingConfig = `gap${MAIN_GAP}`;
const buttonSpacingConfig = {
  buttonSpacing: `mr${MAIN_GAP} -mt-1`,
};
const sizingConfig = 'min-h-40';
interface Props {
  className?: string;
  showFullList?: boolean;
  showRewardsSummary?: boolean;
  showRewardsCondensedInformation?: boolean;
  showRewardsMarketCapitalization?: boolean;
}
const PortfolioCard: FC<Props> = ({
  className,
  showFullList = false,
  showRewardsSummary = false,
  showRewardsCondensedInformation = false,
  showRewardsMarketCapitalization = false,
}) => {
  const router = useRouter();
  const {
    isInitialLoading: initialWalletsLoading,
    isLoading: loading,
    error,
    isUserAuthorized,
    walletsFound,
    tokensOrUnderlyingYieldBearingAssetsFound,
    tokensAndUnderlyingYieldBearingAssets,
    formattedTotalPortfolioValueInUsd,
    formattedTotalPortfolioRewardsValueInUsd,
    underlyingYieldBearingAssetsTotalMarketCap,
    underlyingYieldBearingAssetsTvlWeightedReturn,
    refreshWalletsAndTransactions,
    hasAssetsDeployedInEarn,
    isWalletAndTransactionRefreshing: isWalletAndTransactionRefreshInProgress,
  } = useWalletsContext();
  const { isInitialLoading: initialTransactionsLoading } = useTransactions();
  const isTradeRoute = useMemo(
    () => router.pathname.startsWith(URLS.trade),
    [router.pathname],
  );
  showFullList = showFullList && !isTradeRoute;
  showRewardsMarketCapitalization =
    showRewardsMarketCapitalization || isTradeRoute;
  const noOfItemsToShow = showFullList
    ? 0
    : APP_CONFIG.PORTFOLIO_SHORT_LIST_NO_OF_ASSETS_TO_SHOW;
  const handleRefreshWalletsAndTransactions = useCallback(async () => {
    try {
      logInfo(
        'Refreshing wallets and transactions...',
        'PortfolioCard.handleDownloadPortfolio',
      );
      await refreshWalletsAndTransactions();
      logInfo(
        'Wallets and transactions refresh complete',
        'PortfolioCard.handleDownloadPortfolio',
      );
    } catch (error) {
      logError(error, 'PortfolioCard.handleDownloadPortfolio');
    }
  }, [refreshWalletsAndTransactions]);
  const showSeeAllOrDetailsButton = useMemo(
    () =>
      !initialWalletsLoading &&
      !error &&
      isUserAuthorized &&
      tokensOrUnderlyingYieldBearingAssetsFound &&
      !showFullList,
    [
      initialWalletsLoading,
      error,
      isUserAuthorized,
      tokensOrUnderlyingYieldBearingAssetsFound,
      showFullList,
    ],
  );
  const shouldShowRefreshInProgressIndicator = useMemo(
    () =>
      !initialWalletsLoading &&
      !initialTransactionsLoading &&
      isWalletAndTransactionRefreshInProgress,
    [
      initialWalletsLoading,
      initialTransactionsLoading,
      isWalletAndTransactionRefreshInProgress,
    ],
  );
  const shouldShowRefreshWalletsAndTransactionsButton = useMemo(
    () => !initialWalletsLoading && !initialTransactionsLoading,
    [initialWalletsLoading, initialTransactionsLoading],
  );
  const shouldDisableRefreshWalletsAndTransactionsButton = useMemo(
    () => isWalletAndTransactionRefreshInProgress,
    [isWalletAndTransactionRefreshInProgress],
  );
  const [lastSuccessfulState, setLastSuccessfulState] =
    useState<JSX.Element | null>(null);
  useEffect(() => {
    logInfo(
      'PortfolioCard mounted, resetting lastSuccessfulState',
      'PortfolioCard',
    );
    setLastSuccessfulState(null);
  }, []);
  useEffect(() => {
    logInfo(
      `Wallet states changed: loading=${loading}, initialWalletsLoading=${initialWalletsLoading}, error=${error}, walletsFound=${walletsFound}, tokensOrUnderlyingYieldBearingAssetsFound=${tokensOrUnderlyingYieldBearingAssetsFound}`,
      'PortfolioCard',
    );
  }, [
    loading,
    initialWalletsLoading,
    error,
    walletsFound,
    tokensOrUnderlyingYieldBearingAssetsFound,
  ]);
  const renderBodyContent = useMemo(() => {
    if (initialWalletsLoading) {
      logInfo(
        `Initial loading in progress – this should show loading only if there's truly no data yet`,
        'PortfolioCard',
      );
      return (
        <LoadingAlert
          title={WALLET.LOADING_TITLE}
          description={WALLET.LOADING_DESCRIPTION}
        />
      );
    }
    if (!isUserAuthorized) {
      logError('User is not authorized to use Sliver', 'PortfolioCard');
      return (
        <ErrorAlert
          title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
          description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
        />
      );
    }
    if (error) {
      logError('Error loading portfolio', 'PortfolioCard', { error });
      return (
        <WarningAlert
          title={WALLET.GENERIC_ERROR_LOADING_TITLE}
          description={WALLET.GENERIC_ERROR_LOADING_DESCRIPTION}
        />
      );
    }
    if (!loading && !walletsFound) {
      logWarning('Loading is done, but no wallets found', 'PortfolioCard');
      return (
        <WarningAlert
          title={WALLET.WALLET_NOT_FOUND_TITLE}
          description={WALLET.WALLET_NOT_FOUND_DESCRIPTION}
        />
      );
    }
    if (
      !loading &&
      walletsFound &&
      !tokensOrUnderlyingYieldBearingAssetsFound
    ) {
      logWarning(
        'Loading is done, wallet found but no assets found',
        'PortfolioCard',
      );
      return (
        <WarningAlert
          title={WALLET.WALLET_FOUND_BUT_NO_ASSETS_FOUND_TITLE}
          description={WALLET.WALLET_FOUND_BUT_NO_ASSETS_FOUND_DESCRIPTION}
        />
      );
    }
    return (
      <div
        className={`flex flex-col ${spacingConfig} ${sizingConfig} ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {showRewardsSummary && hasAssetsDeployedInEarn() && (
          <RewardsSummarySection
            formattedTotalRewardsValueInUsd={
              formattedTotalPortfolioRewardsValueInUsd
            }
            blendedReturn={underlyingYieldBearingAssetsTvlWeightedReturn}
            blendedMarketCap={underlyingYieldBearingAssetsTotalMarketCap}
            showCondensedInformation={showRewardsCondensedInformation}
          />
        )}
        <PortfolioAssetsSection
          assets={
            tokensAndUnderlyingYieldBearingAssets as WalletUnderlyingYieldBearingAssetInterface[]
          }
          noOfItemsToShow={noOfItemsToShow}
          showShortList={!showFullList}
          showSeeAllOrDetailsButton={showSeeAllOrDetailsButton}
        />
      </div>
    );
  }, [
    isUserAuthorized,
    initialWalletsLoading,
    error,
    walletsFound,
    tokensOrUnderlyingYieldBearingAssetsFound,
    loading,
    showRewardsSummary,
    hasAssetsDeployedInEarn,
    formattedTotalPortfolioRewardsValueInUsd,
    underlyingYieldBearingAssetsTvlWeightedReturn,
    underlyingYieldBearingAssetsTotalMarketCap,
    showRewardsCondensedInformation,
    tokensAndUnderlyingYieldBearingAssets,
    noOfItemsToShow,
    showFullList,
    showSeeAllOrDetailsButton,
  ]);
  useEffect(() => {
    if (
      !initialWalletsLoading &&
      !loading &&
      !(renderBodyContent?.type === LoadingAlert)
    ) {
      setLastSuccessfulState(renderBodyContent);
    }
  }, [renderBodyContent, initialWalletsLoading, loading]);
  const renderHeader = () => (
    <Card.Header
      title={isUserAuthorized ? formattedTotalPortfolioValueInUsd : '$0.00'}
      subTitle="Portfolio"
      loading={shouldShowRefreshInProgressIndicator}
      loadingIsButton={false}
      leftNodeElements={[]}
      rightNodeElements={[
        shouldShowRefreshWalletsAndTransactionsButton && (
          <DownloadFromCloudButton
            key="download_portfolio_button"
            onDownloadClick={handleRefreshWalletsAndTransactions}
            intent="ghost"
            className={buttonSpacingConfig.buttonSpacing}
            disabled={shouldDisableRefreshWalletsAndTransactionsButton}
          />
        ),
        <AnimationProvider key="deposit">
          <DepositToWalletButton />
        </AnimationProvider>,
        <AnimationProvider key="withdraw">
          <WithdrawFromWalletButton />
        </AnimationProvider>,
        !isTradeRoute && (
          <AnimationProvider key="trade">
            <TradeButton />
          </AnimationProvider>
        ),
        <AnimationProvider key="earn">
          <EarnButton />
        </AnimationProvider>,
      ].filter(Boolean)}
      className={`${borderClassToVisualizeElementsAndSpacing}`}
    />
  );
  return (
    <Card className={className}>
      {renderHeader()}
      <Card.Body>
        {loading && !initialWalletsLoading && lastSuccessfulState
          ? lastSuccessfulState
          : renderBodyContent}
      </Card.Body>
    </Card>
  );
};
export default PortfolioCard;
